import { FF_utils } from './lib/utils.js';
import validateEmail from './lib/validateEmail.js';

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

interface DemoResponse {
	ticket?: unknown;
	error?: string;
}

class ffBookDemo {
	submissionId: string;

	state = {
		tried: false,
		working: false,
		valid: false,
		success: false,
		messages: [] as string[],
	};

	DOM: {
		form: HTMLFormElement;
		body: HTMLElement;
		submits: NodeListOf<HTMLButtonElement>;
		products: NodeListOf<HTMLInputElement>;
		productGrid: HTMLElement | null;
		payloads: HTMLElement | null;
		details: HTMLElement | null;
		errorMessage: HTMLElement;
		successMessage: HTMLElement;
		spinner: HTMLElement;
		email: HTMLInputElement;
	} = {} as ffBookDemo['DOM'];

	constructor() {
		const ff_utils = new FF_utils();
		this.submissionId = ff_utils.cuid();
		const form = document.querySelector('.js-demoForm');
		if (!form) return;
		this.DOM = {
			form: form as HTMLFormElement,
			body: form.querySelector('.js-demoBody') as HTMLElement,
			submits: form.querySelectorAll('button[type="submit"]'),
			products: form.querySelectorAll('input[name="products[]"]'),
			productGrid: form.querySelector('.productGrid'),
			payloads: form.querySelector('.js-demoPayloads'),
			details: form.querySelector('.js-demoDetails'),
			errorMessage: form.querySelector('.js-errorMessage') as HTMLElement,
			successMessage: form.querySelector('.js-successMessage') as HTMLElement,
			spinner: form.querySelector('.js-spinner') as HTMLElement,
			email: form.querySelector('input[name=email]') as HTMLInputElement,
		};
		this.DOM.form.addEventListener('submit', this.submit.bind(this));
		this.DOM.form.addEventListener('input', this.reValidate.bind(this));
		this.prepareProducts();
		this.preparePayloads();
		this.syncPayloads();
	}

	prepareProducts() {
		const grid = this.DOM.productGrid;
		if (!grid) return;

		let pickedWithPointer = false;
		let chosenByKeyboard = false;
		grid.addEventListener('pointerdown', () => {
			pickedWithPointer = true;
		});
		grid.addEventListener('pointerup', () => {
			window.setTimeout(() => {
				pickedWithPointer = false;
			}, 0);
		});
		grid.addEventListener('focusout', (event) => {
			const next = event.relatedTarget;
			if (next instanceof Node && grid.contains(next)) return;
			if (!chosenByKeyboard) return;
			chosenByKeyboard = false;
			this.scrollToNext();
		});

		for (const input of this.DOM.products) {
			input.addEventListener('change', () => {
				this.syncPayloads();
				if (pickedWithPointer) {
					pickedWithPointer = false;
					chosenByKeyboard = false;
					this.scrollToNext();
					return;
				}
				chosenByKeyboard = true;
			});
		}
	}

	preparePayloads() {
		const section = this.DOM.payloads;
		if (!section) return;

		section.addEventListener('change', (event) => {
			const input = event.target;
			if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox' || !input.checked) {
				return;
			}
			const group = input.closest<HTMLElement>('[data-payload-product]');
			if (!group) return;

			if (input.value === 'not-sure') {
				group.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((other) => {
					if (other !== input) other.checked = false;
				});
				return;
			}

			const unsure = group.querySelector<HTMLInputElement>('input[value="not-sure"]');
			if (unsure) unsure.checked = false;
		});
	}

	syncPayloads() {
		const section = this.DOM.payloads;
		if (!section) return;

		const selected = Array.from(this.DOM.products).find((input) => input.checked)?.value ?? '';
		let visible = false;

		section.querySelectorAll<HTMLElement>('[data-payload-product]').forEach((group) => {
			const active = group.dataset.payloadProduct === selected;
			group.hidden = !active;
			if (active) visible = true;
			group.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
				input.disabled = !active;
				if (!active) input.checked = false;
			});
		});

		section.hidden = !visible;
	}

	selectedProduct() {
		return Array.from(this.DOM.products).some((input) => input.checked);
	}

	scrollToNext() {
		const payloads = this.DOM.payloads;
		const target = payloads && !payloads.hidden ? payloads : this.DOM.details;
		if (!target) return;
		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		target.scrollIntoView({
			behavior: reduceMotion ? 'auto' : 'smooth',
			block: 'start',
		});
	}

	submit(e: SubmitEvent) {
		e.preventDefault();
		this.state.tried = true;
		if (this.state.success || this.state.working) return;

		if (!this.validate()) {
			this.render();
			this.DOM.errorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
			return;
		}

		this.state.working = true;
		this.render();

		if (typeof grecaptcha === 'undefined') {
			this.state.messages = [
				'Unable to verify your submission. Please disable ad blockers and try again.',
			];
			this.state.valid = false;
			this.state.working = false;
			this.render();
			return;
		}

		const formData = new FormData(this.DOM.form);
		formData.append('action', 'demo_submit');
		formData.append('id', this.submissionId);

		grecaptcha.ready(() => {
			grecaptcha
				.execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
				.then((token: string) => {
					formData.append('g-recaptcha-response', token);
					return fetch(ajaxurl, {
						method: 'POST',
						body: formData,
					}).then(this.formResponse.bind(this));
				})
				.catch(() => {
					this.state.messages = ['Unable to verify your submission. Please try again.'];
					this.state.valid = false;
					this.state.working = false;
					this.render();
				});
		});
	}

	validate() {
		const messages: string[] = [];
		if (!this.selectedProduct()) {
			messages.push('Please select a product.');
		}
		const required = this.DOM.form.querySelectorAll<HTMLInputElement>(
			'input[name=firstName], input[name=lastName], input[name=email], input[name=phone], input[name=city], input[name=province], input[name=country]'
		);
		for (const input of required) {
			if (input.value.trim() === '') {
				messages.push('Please complete all required fields.');
				break;
			}
		}
		if (this.DOM.email.value.trim() !== '' && !validateEmail(this.DOM.email.value.trim())) {
			messages.push('Enter a valid email address.');
		}
		this.state.messages = messages;
		this.state.valid = messages.length === 0;
		return this.state.valid;
	}

	reValidate() {
		if (!this.state.tried || this.state.success) return;
		this.validate();
		this.render();
	}

	render() {
		const locked =
			this.state.working || this.state.success || (this.state.tried && !this.state.valid);
		this.DOM.submits.forEach((button) => {
			button.disabled = locked;
		});

		if (this.state.tried && !this.state.valid && this.state.messages.length > 0) {
			const items = this.state.messages
				.map((message) => `<li>${escapeHtml(message)}</li>`)
				.join('');
			this.DOM.errorMessage.innerHTML = `<h2>Error</h2><ul>${items}</ul>`;
			this.DOM.errorMessage.classList.remove('js-hide');
		} else {
			this.DOM.errorMessage.classList.add('js-hide');
		}

		this.DOM.spinner.classList.toggle('js-hide', !this.state.working);

		if (this.state.success) {
			this.DOM.body.classList.add('is-complete');
			this.DOM.body.setAttribute('inert', '');
			this.DOM.successMessage.classList.remove('js-hide');
		}
	}

	async formResponse(res: Response) {
		this.state.working = false;
		let data: DemoResponse | undefined;
		try {
			data = await res.json();
		} catch {
			this.state.messages = ['Request could not be processed.'];
			this.state.valid = false;
			this.state.tried = true;
			this.render();
			return;
		}
		if (data?.ticket) {
			this.state.success = true;
			this.state.valid = true;
			this.state.messages = [];
		} else {
			this.state.messages = [data?.error || 'Request could not be processed.'];
			this.state.valid = false;
			this.state.tried = true;
		}
		this.render();
	}
}

new ffBookDemo();
